/**
 * Query understanding — identifier lookup, natural-language intent parsing, filtering.
 *
 * A faithful port of the legacy `atlas.js` dispatch logic. Behaviour is pinned by
 * `test/golden/parity.test.ts` against snapshots of the v2.2 build; the two deliberate
 * deviations from that baseline are marked DEVIATION below and listed in the test.
 */
import type { Asset, Filters } from './types.ts';

/**
 * Resolve a query to a single cached asset by any identifier it carries.
 *
 * Matching is case-insensitive and ordered: exact ticker, code, ISIN, Bloomberg, and
 * full name, then a substring match on the name. The substring pass requires 4+
 * characters so short tickers cannot pull in an unrelated issuer. First match in
 * universe order wins.
 */
export function findAsset(assets: readonly Asset[], query: string): Asset | undefined {
  const q = query.toUpperCase().trim();
  if (!q) return undefined;
  return assets.find((a) => {
    if (a.tickers?.some((t) => t.toUpperCase() === q)) return true;
    if (a.code?.toUpperCase() === q) return true;
    if (a.isin?.toUpperCase() === q) return true;
    if (a.bloomberg?.toUpperCase() === q) return true;
    if (a.name?.toUpperCase() === q) return true;
    if (q.length >= 4 && a.name?.toUpperCase().includes(q)) return true;
    return false;
  });
}

/**
 * Corridor synonyms, tried in order — the first hit wins, matching the legacy
 * if/else-if chain.
 *
 * DEVIATION (corridor-coverage): the legacy table stopped at Indonesia, so the two
 * newest corridors had no pattern at all. `South Africa` (10 assets) and `Vietnam`
 * (1) silently fell through to "no corridor filter", meaning a query for either
 * returned the entire 101-asset universe. The last two rows fix that.
 */
const CORRIDOR_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b(?:brazil|b3|brasil)\b/, 'Brazil'],
  [/\b(?:ksa|saudi|tadawul)\b/, 'Saudi Arabia'],
  [/\b(?:uae|dubai|emirates|dfm|adx|abu dhabi)\b/, 'United Arab Emirates'],
  [/\b(?:malaysia|malayan|bursa)\b/, 'Malaysia'],
  [/\b(?:singapore|sgx)\b/, 'Singapore'],
  [/\b(?:indonesia|jakarta|idx)\b/, 'Indonesia'],
  [/\b(?:south africa|jse|johannesburg)\b/, 'South Africa'],
  [/\b(?:vietnam|viet nam|hose|ho chi minh)\b/, 'Vietnam'],
];

const SLEEVE_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b(?:anchor)\b/, 'Anchor'],
  [/\b(?:high[\s-]?yield|highyield)\b/, 'High-Yield'],
  [/\b(?:diversifier)\b/, 'Diversifier'],
];

/**
 * Extract filter intent from a natural-language query.
 *
 * Returns `isListIntent: false` and nothing else when the query carries no recognisable
 * intent — the caller treats that as "score this off-cache name via the backend".
 */
export function parsePrompt(query: string): Filters {
  const q = query.toLowerCase();
  const f: Filters = { isListIntent: false };

  const topMatch = q.match(/\b(?:top|best|highest)\s+(\d+)\b/);
  if (topMatch?.[1]) {
    f.limit = Number.parseInt(topMatch[1], 10);
    f.sortBy = 'score';
    f.isListIntent = true;
  }
  if (/\b(?:top|best|highest|all|every|list|show me|give me)\b/.test(q)) {
    f.isListIntent = true;
    f.sortBy ??= 'score';
  }

  for (const [re, corridor] of CORRIDOR_PATTERNS) {
    if (re.test(q)) {
      f.corridor = corridor;
      break;
    }
  }
  for (const [re, sleeve] of SLEEVE_PATTERNS) {
    if (re.test(q)) {
      f.sleeve = sleeve;
      break;
    }
  }

  if (/\b(?:sharia|islamic|halal|aaoifi)\b/.test(q)) f.sharia = true;
  if (/\b(?:dividend|yield(?!-channel))\b/.test(q) && !f.sortBy) {
    f.sortBy = 'yield';
    f.isListIntent = true;
  }
  if (/\b(?:equity|equities|stocks?)\b/.test(q)) f.assetClass = 'Listed Equity';

  const scoreMatch = q.match(/\b(?:above|over|score)\s+(\d+)\b/);
  if (scoreMatch?.[1]) f.scoreMin = Number.parseInt(scoreMatch[1], 10);

  // NOTE: matches the bare word, so "not eligible" also sets status 'Eligible'.
  // Preserved from the legacy build — see the parity test's KNOWN_QUIRKS.
  if (/\b(?:eligible)\b/.test(q)) f.status = 'Eligible';

  return f;
}

/**
 * Does this parse carry enough intent to be treated as a filter query?
 *
 * Deliberately falsy-tested (not nullish), matching the legacy `||` chain: a
 * `sharia: false` or `limit: 0` must fall through to off-cache scoring rather than
 * count as intent.
 */
export function hasFilterIntent(f: Filters): boolean {
  return Boolean(
    f.corridor ||
      f.sleeve ||
      f.sharia ||
      f.status ||
      f.assetClass ||
      f.scoreMin !== undefined ||
      f.sortBy ||
      f.isListIntent ||
      f.limit,
  );
}

function dividendYield(a: Asset): number {
  const raw = a.fact_sheet?.dividend_yield;
  const n = raw === undefined ? Number.NaN : Number.parseFloat(raw);
  return Number.isNaN(n) ? 0 : n;
}

/** Apply a parsed filter set. Pure — never mutates the input list. */
export function applyFilters(list: readonly Asset[], f: Filters): Asset[] {
  let r = [...list];
  if (f.corridor) r = r.filter((a) => a.corridor === f.corridor);
  if (f.sleeve) r = r.filter((a) => a.sleeve === f.sleeve);
  if (f.sharia === true) r = r.filter((a) => a.sharia === true);
  if (f.status) r = r.filter((a) => a.status === f.status);
  if (f.assetClass) r = r.filter((a) => a.asset_class === f.assetClass);
  const { scoreMin } = f;
  if (typeof scoreMin === 'number') r = r.filter((a) => a.score >= scoreMin);

  if (f.sortBy === 'score') r.sort((a, b) => b.score - a.score);
  else if (f.sortBy === 'yield') r.sort((a, b) => dividendYield(b) - dividendYield(a));

  if (typeof f.limit === 'number') r = r.slice(0, f.limit);
  return r;
}

/** Split a comma/semicolon/newline separated query into trimmed tokens. */
export function splitMultiQuery(query: string): string[] {
  return query
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}
